import { UserType } from 'interfaces/user';
import { useMemo } from 'react';
import { Role } from 'generated/graphql';
import { useFeatures } from './useFeatures';
import useUser from './useUser';

export enum Permission {
  Finance = 'Finance',
  MarketingTools = 'MarketingTools',
  ProductConfig = 'ProductConfig',
  Customers = 'Customers',
  Market = 'Market',
  CHEPLocations = 'CHEPLocations',
  Dashboard = 'Dashboard', // Note: Permission to show or hide the reports tab (i.e., pricing dashboard) on the reports screen
  Packhouse = 'Packhouse', // Note: Permission to show or hide the packhouse tab on the reports screen
  ProductMarkups = 'ProductMarkups',
  Setup = 'Setup',
  SellerLogistics = 'SellerLogistics',
  SellerConfirmedOrders = 'SellerConfirmedOrders',
  SellerPayments = 'SellerPayments',
  SellerLoadouts = 'SellerLoadouts',
  Reports = 'Reports', // Note: Permission to hide or show the reports tab in the navigation items (Top and bottom navbar)
  Operations = 'Operations',
  Sales = 'Sales',
  QuotesAllBuyers = 'QuotesAllBuyers', // Note: Browse and act on quotes for any buyer, not only the user's own profile
}

export enum Scope {
  Read = 'Read',
  Write = 'Write',
}

const SellerRolePermissions = new Map<Scope, Permission[]>([
  [Scope.Read, [Permission.SellerLogistics, Permission.SellerPayments, Permission.Reports]],
  [Scope.Write, [Permission.SellerLogistics, Permission.Reports]],
]);

const MarketRolePermissions = new Map<Scope, Permission[]>([
  [Scope.Read, [Permission.Market]],
  [Scope.Write, [Permission.Market]],
]);

const AdminRolePermissions = new Map<Scope, Permission[]>([
  [Scope.Read, [Permission.Customers, Permission.MarketingTools, Permission.ProductConfig, Permission.ProductMarkups]],
  [Scope.Write, [Permission.MarketingTools, Permission.ProductMarkups]],
]);

const AdminCustomerRolePermissions = new Map<Scope, Permission[]>([
  [Scope.Read, [Permission.Customers]],
  [Scope.Write, [Permission.Customers]],
]);

const AdminProductRolePermissions = new Map<Scope, Permission[]>([
  [Scope.Read, [Permission.ProductConfig]],
  [Scope.Write, [Permission.ProductConfig]],
]);

const FinanceOwnerRolePermissions = new Map<Scope, Permission[]>([
  [Scope.Read, [Permission.Finance, Permission.CHEPLocations]],
  [Scope.Write, [Permission.Finance, Permission.CHEPLocations]],
]);

const FinanceMemberRolePermissions = new Map<Scope, Permission[]>([
  [Scope.Read, [Permission.Finance]],
  [Scope.Write, [Permission.Finance]],
]);

const ProfileOwnerPermissions = new Map<Scope, Permission[]>([
  [Scope.Read, [Permission.Finance]],
  [Scope.Write, [Permission.Finance]],
]);

// CustomerProfileOwner lets staff control a customer's profile, so it grants what the customer's own owner and seller
// get. Buyer has no client permissions.
const CustomerProfileOwnerPermissions = new Map<Scope, Permission[]>(
  [Scope.Read, Scope.Write].map((scope) => [
    scope,
    [...(ProfileOwnerPermissions.get(scope) ?? []), ...(SellerRolePermissions.get(scope) ?? [])],
  ]),
);

const DashboardRolePermissions = new Map<Scope, Permission[]>([
  [Scope.Read, [Permission.Dashboard, Permission.Setup]],
  [Scope.Write, [Permission.Dashboard, Permission.Setup]],
]);

const PackhouseRolePermissions = new Map<Scope, Permission[]>([
  [Scope.Read, [Permission.Dashboard, Permission.Packhouse, Permission.SellerLoadouts, Permission.Reports]],
  [Scope.Write, [Permission.Dashboard, Permission.Packhouse, Permission.SellerLoadouts, Permission.Reports]],
]);

const PackhouseWorkerRolePermissions = new Map<Scope, Permission[]>([
  [Scope.Read, [Permission.Packhouse, Permission.SellerConfirmedOrders, Permission.Reports]],
  [Scope.Write, []],
]);

const OperationsRolePermissions = new Map<Scope, Permission[]>([
  [Scope.Read, [Permission.Operations]],
  [Scope.Write, [Permission.Operations]],
]);

const SalesRolePermissions = new Map<Scope, Permission[]>([
  [Scope.Read, [Permission.Sales]],
  [Scope.Write, [Permission.Sales]],
]);

const QuotesRolePermissions = new Map<Scope, Permission[]>([
  [Scope.Read, [Permission.QuotesAllBuyers]],
  [Scope.Write, [Permission.QuotesAllBuyers]],
]);

const roleHasPermission = (role: Role, permission: Permission, scope: Scope): boolean => {
  switch (role) {
    case Role.Super:
      return true;
    case Role.Seller:
      return !!SellerRolePermissions.get(scope)?.includes(permission);
    case Role.Market:
      return !!MarketRolePermissions.get(scope)?.includes(permission);
    case Role.Admin:
      return !!AdminRolePermissions.get(scope)?.includes(permission);
    case Role.AdminCustomer:
      return !!AdminCustomerRolePermissions.get(scope)?.includes(permission);
    case Role.AdminProduct:
      return !!AdminProductRolePermissions.get(scope)?.includes(permission);
    case Role.FinanceOwner:
      return !!FinanceOwnerRolePermissions.get(scope)?.includes(permission);
    case Role.FinanceMember:
      return !!FinanceMemberRolePermissions.get(scope)?.includes(permission);
    case Role.ProfileOwner:
      return !!ProfileOwnerPermissions.get(scope)?.includes(permission);
    case Role.CustomerProfileOwner:
      return !!CustomerProfileOwnerPermissions.get(scope)?.includes(permission);
    case Role.Dashboard:
      return !!DashboardRolePermissions.get(scope)?.includes(permission);
    case Role.Packhouse:
      return !!PackhouseRolePermissions.get(scope)?.includes(permission);
    case Role.PackhouseWorker:
      return !!PackhouseWorkerRolePermissions.get(scope)?.includes(permission);
    case Role.Operations:
      return !!OperationsRolePermissions.get(scope)?.includes(permission);
    case Role.Sales:
      return !!SalesRolePermissions.get(scope)?.includes(permission);
    case Role.Quotes:
      return !!QuotesRolePermissions.get(scope)?.includes(permission);
    default:
      return false;
  }
};

export const useAccess = () => {
  const { user } = useUser();
  return useMemo(
    () => (permission: Permission, scope: Scope) => {
      for (let role of user?.roles ?? []) {
        // On the admin profile CustomerProfileOwner grants only the seller's permissions, which open a customer's
        // screens such as a seller's locations. ProfileOwner's Finance shares its name with the admin finance
        // screens, so it applies only once the user acts as the customer.
        if (role === Role.CustomerProfileOwner && user?.userType === UserType.Admin) {
          if (SellerRolePermissions.get(scope)?.includes(permission)) {
            return true;
          }
          continue;
        }
        if (roleHasPermission(role, permission, scope)) {
          return true;
        }
      }
      return false;
    },
    [user],
  );
};
